// Admin coverage-map sub-tab. Three panels:
//   1. Map (full width): ZIP polygons shaded by phleb count. Click a ZIP to see who covers it.
//   2. Recent onboards: 10 most recent phlebs who completed onboarding. Click → highlights their ZIPs on the map.
//   3. Coverage stats: KPI tiles (covered/uncovered ZIPs, avg phlebs per ZIP, top phleb, etc.)
//
// Reuses Steve's ServiceZipPicker approach (Google Maps Data layer + city GeoJSON URL),
// driven by the configurable city registry in src/utils/cityCoverage.js.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Heading, Card, CardBody, Badge, Spinner,
  Icon, Button, Select, SimpleGrid, Divider, Tooltip, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, Tag, Wrap, WrapItem, Alert, AlertIcon,
} from '@chakra-ui/react';
import { FaMapMarkedAlt, FaUserPlus, FaCheckCircle, FaExclamationTriangle, FaUsers, FaTrophy, FaBoxOpen } from 'react-icons/fa';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { supabase } from '../../supabaseClient';
import { getCities, getCity, getDefaultCityId } from '../../utils/cityCoverage';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../utils/googleMapsLoader';

// Property names different GeoJSON sources use for the ZIP code. One canonical
// list — both the Data-layer feature getter and the plain-properties extractor below
// must read from the same set, otherwise the polygon shading and the uncovered-ZIPs
// list will disagree.
const ZIP_PROPERTY_KEYS = [
  'modzcta', 'MODZCTA', 'zcta', 'ZCTA', 'ZIPCODE', 'zipcode', 'postal_code', 'POSTALCODE',
];

// Look at a Data layer Feature and try to extract its ZIP code.
function tryZipProperties(feature) {
  for (const k of ZIP_PROPERTY_KEYS) {
    const v = feature.getProperty(k);
    if (v != null && v !== '') return String(v).trim();
  }
  return '';
}

// Same logic but for a plain GeoJSON feature properties object (used when we
// iterate geojson.features without a Data layer).
function zipFromProperties(props) {
  if (!props) return '';
  for (const k of ZIP_PROPERTY_KEYS) {
    if (props[k] != null && props[k] !== '') return String(props[k]).trim();
  }
  return '';
}

// Shade for a ZIP based on how many phlebs cover it.
function fillForCount(count) {
  if (!count) return '#e2e8f0';        // gray-200 — uncovered
  if (count === 1) return '#bee3f8';   // blue-100
  if (count === 2) return '#90cdf4';   // blue-200
  if (count === 3) return '#63b3ed';   // blue-300
  if (count <= 5) return '#3182ce';    // blue-500
  return '#1e3a8a';                    // blue-900 — heavy coverage
}

export default function PhlebCoverageMap() {
  const [cityId, setCityId] = useState(getDefaultCityId());
  const city = useMemo(() => getCity(cityId), [cityId]);

  const [phlebs, setPhlebs] = useState([]);
  const [loadingPhlebs, setLoadingPhlebs] = useState(true);
  const [geojson, setGeojson] = useState(null);
  const [geojsonError, setGeojsonError] = useState(null);
  const [focusedPhlebId, setFocusedPhlebId] = useState(null); // phleb whose ZIPs get the orange overlay
  const [focusedZip, setFocusedZip] = useState(null);          // ZIP whose detail modal is open
  const [mapMode, setMapMode] = useState('coverage');          // 'coverage' | 'supplies'

  const mapRef = useRef(null);
  const dataLayerListenersRef = useRef([]);

  const { isOpen: isUncoveredOpen, onOpen: openUncovered, onClose: closeUncovered } = useDisclosure();
  const { isOpen: isZipOpen, onOpen: openZip, onClose: closeZip } = useDisclosure();

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  // Load all phlebs who have completed onboarding. service_zips is the JSONB list
  // they picked during the Service Area step of the wizard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPhlebs(true);
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('id, full_name, company_name, email, service_zips, onboarding_status, onboarding_completed_at, created_at, supplies_picked_up_at')
        .eq('onboarding_status', 'completed')
        .order('onboarding_completed_at', { ascending: false, nullsFirst: false });
      if (cancelled) return;
      if (error) {
        console.warn('Could not load phlebs for coverage map:', error);
        setPhlebs([]);
      } else {
        setPhlebs(data || []);
      }
      setLoadingPhlebs(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the city's ZIP GeoJSON
  useEffect(() => {
    let cancelled = false;
    setGeojson(null);
    setGeojsonError(null);
    (async () => {
      try {
        const res = await fetch(city.geojsonUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setGeojson(data);
      } catch (err) {
        if (!cancelled) setGeojsonError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [city.geojsonUrl]);

  // ZIP -> list of phlebs covering it
  const zipToPhlebs = useMemo(() => {
    const map = new Map();
    phlebs.forEach((p) => {
      const zips = Array.isArray(p.service_zips) ? p.service_zips : [];
      zips.forEach((z) => {
        const key = String(z);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(p);
      });
    });
    return map;
  }, [phlebs]);

  // ── Mount polygons + click listener ONCE when geojson + map are both ready.
  // Subsequent focus/zipToPhlebs changes only call setStyle (below), which avoids
  // the flicker of tearing down every feature on every click.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geojson) return;
    // Clean slate in case geojson changed (city switch)
    map.data.forEach((f) => map.data.remove(f));
    dataLayerListenersRef.current.forEach((l) => l.remove());
    dataLayerListenersRef.current = [];
    map.data.addGeoJson(geojson);

    const clickListener = map.data.addListener('click', (e) => {
      const zip = tryZipProperties(e.feature);
      if (zip) {
        setFocusedZip(zip);
        openZip();
      }
    });
    dataLayerListenersRef.current.push(clickListener);

    // Cleanup on unmount or geojson change — drop features + listeners so we don't
    // leak handlers bound to the disposed map instance.
    return () => {
      try {
        dataLayerListenersRef.current.forEach((l) => l.remove());
        dataLayerListenersRef.current = [];
        if (mapRef.current) {
          mapRef.current.data.forEach((f) => mapRef.current.data.remove(f));
        }
      } catch { /* map already torn down — nothing to do */ }
    };
  }, [geojson, openZip]);

  // ── Restyle polygons whenever the coverage map or focused phleb changes.
  // No add/remove, no listener churn — pure styling pass, fast.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geojson) return;
    const focusedPhleb = focusedPhlebId ? phlebs.find((p) => p.id === focusedPhlebId) : null;
    const focusedZipsSet = focusedPhleb
      ? new Set((focusedPhleb.service_zips || []).map(String))
      : null;
    map.data.setStyle((feature) => {
      const zip = tryZipProperties(feature);
      const list = zip && zipToPhlebs.has(zip) ? zipToPhlebs.get(zip) : [];
      const count = list.length;
      const focused = focusedZipsSet && focusedZipsSet.has(zip);

      // Focused phleb always wins — orange overlay on their ZIPs.
      if (focused) {
        return { fillColor: '#dd6b20', fillOpacity: 0.75, strokeColor: '#9c4221', strokeWeight: 1.5 };
      }

      // Supply-readiness mode: shade ZIPs by how many *supply-ready* phlebs cover
      // them; dim ZIPs that are covered only by phlebs still awaiting supplies.
      if (mapMode === 'supplies') {
        const readyCount = list.filter((p) => p.supplies_picked_up_at).length;
        if (readyCount > 0) {
          return { fillColor: fillForCount(readyCount), fillOpacity: 0.6, strokeColor: '#4a5568', strokeWeight: 0.6 };
        }
        if (count > 0) {
          // Covered on paper, but no one here has their kit yet — dim amber.
          return { fillColor: '#ecc94b', fillOpacity: 0.22, strokeColor: '#b7791f', strokeWeight: 0.6 };
        }
        return { fillColor: '#e2e8f0', fillOpacity: 0.18, strokeColor: '#4a5568', strokeWeight: 0.6 };
      }

      // Default coverage mode: shade by total phleb count.
      return {
        fillColor: fillForCount(count),
        fillOpacity: count ? 0.55 : 0.18,
        strokeColor: '#4a5568',
        strokeWeight: 0.6,
      };
    });
  }, [geojson, zipToPhlebs, focusedPhlebId, phlebs, mapMode]);

  // Clear mapRef on unmount so we don't keep a stale reference around
  useEffect(() => () => { mapRef.current = null; }, []);

  // The canonical set of ZIPs in this city (from the GeoJSON the map uses, or the
  // fallback list when the GeoJSON request fails). Everything downstream — coverage
  // counts, uncovered list, stats — derives from this single source so the numbers
  // can't disagree with the polygons rendered on the map.
  const cityZipSet = useMemo(() => {
    const list = geojson
      ? geojson.features.map((f) => zipFromProperties(f.properties)).filter(Boolean)
      : city.fallbackZips;
    return new Set(list.map(String));
  }, [geojson, city]);

  // Stats — intersect coverage with the city's actual ZIPs so coverage can never exceed 100%.
  const stats = useMemo(() => {
    const totalZipsInCity = cityZipSet.size || city.fallbackZips.length;
    let coveredInCity = 0;
    let coveredPhlebTotal = 0;
    zipToPhlebs.forEach((list, zip) => {
      if (cityZipSet.has(zip)) {
        coveredInCity++;
        coveredPhlebTotal += list.length;
      }
    });
    const uncovered = Math.max(0, totalZipsInCity - coveredInCity);
    const avg = coveredInCity ? coveredPhlebTotal / coveredInCity : 0;
    const topPhleb = phlebs.slice().sort(
      (a, b) => (b.service_zips?.length || 0) - (a.service_zips?.length || 0),
    )[0];
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const newThisWeek = phlebs.filter((p) => {
      const t = p.onboarding_completed_at ? new Date(p.onboarding_completed_at).getTime() : 0;
      return t >= sevenDaysAgo;
    }).length;
    const suppliesPickedUp = phlebs.filter((p) => p.supplies_picked_up_at).length;
    return {
      totalZipsInCity, coveredZips: coveredInCity, uncovered, avg, topPhleb, newThisWeek, suppliesPickedUp,
    };
  }, [zipToPhlebs, phlebs, cityZipSet, city]);

  // Uncovered ZIP list — derived from the same city set as the stats so the numbers match.
  const uncoveredZips = useMemo(
    () => Array.from(cityZipSet).filter((z) => !zipToPhlebs.has(z)).sort(),
    [cityZipSet, zipToPhlebs],
  );

  const cities = getCities();

  return (
    <Box>
      {/* Header row — city selector + summary */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={3} mb={4}>
        <HStack spacing={3}>
          <Box bg="blue.50" color="blue.600" p={2} borderRadius="md">
            <Icon as={FaMapMarkedAlt} boxSize={4} />
          </Box>
          <Box>
            <Heading size="md" color="gray.800">Phleb Coverage Map</Heading>
            <Text fontSize="xs" color="gray.500">
              {loadingPhlebs ? 'Loading…' : `${phlebs.length} onboarded phleb${phlebs.length === 1 ? '' : 's'} · covering ${stats.coveredZips}/${stats.totalZipsInCity} ZIPs`}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={3}>
          <HStack spacing={0} border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
            <Button
              size="sm"
              borderRadius={0}
              variant={mapMode === 'coverage' ? 'solid' : 'ghost'}
              colorScheme={mapMode === 'coverage' ? 'blue' : 'gray'}
              onClick={() => setMapMode('coverage')}
            >
              Coverage
            </Button>
            <Button
              size="sm"
              borderRadius={0}
              variant={mapMode === 'supplies' ? 'solid' : 'ghost'}
              colorScheme={mapMode === 'supplies' ? 'teal' : 'gray'}
              onClick={() => setMapMode('supplies')}
              leftIcon={<Icon as={FaBoxOpen} boxSize={3} />}
            >
              Supply readiness
            </Button>
          </HStack>
          {cities.length > 1 && (
            <Select size="sm" w="200px" value={cityId} onChange={(e) => setCityId(e.target.value)}>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          )}
        </HStack>
      </Flex>

      {geojsonError && (
        <Alert status="warning" mb={3} borderRadius="md" fontSize="sm">
          <AlertIcon />
          Could not load city ZIP polygons ({geojsonError}). Map is empty; stats below still work from the phleb data.
        </Alert>
      )}

      {/* Map */}
      <Box mb={4} borderRadius="xl" overflow="hidden" boxShadow="md" border="1px solid" borderColor="gray.200">
        {!isLoaded ? (
          <Flex h="520px" align="center" justify="center" bg="gray.50">
            <Spinner color="blue.500" />
          </Flex>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '520px' }}
            center={city.center}
            zoom={city.zoom}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
            onLoad={(m) => { mapRef.current = m; }}
          />
        )}
      </Box>

      {/* Legend — explains the shading for the active map mode */}
      <HStack spacing={4} mb={4} fontSize="xs" color="gray.600" flexWrap="wrap">
        {mapMode === 'supplies' ? (
          <>
            <HStack spacing={1.5}>
              <Box w={3} h={3} borderRadius="sm" bg="#3182ce" />
              <Text>Serviceable (a phleb here has supplies)</Text>
            </HStack>
            <HStack spacing={1.5}>
              <Box w={3} h={3} borderRadius="sm" bg="#ecc94b" opacity={0.5} />
              <Text>Covered but awaiting supplies</Text>
            </HStack>
            <HStack spacing={1.5}>
              <Box w={3} h={3} borderRadius="sm" bg="#e2e8f0" />
              <Text>Uncovered</Text>
            </HStack>
          </>
        ) : (
          <>
            <HStack spacing={1.5}>
              <Box w={3} h={3} borderRadius="sm" bg="#bee3f8" />
              <Text>1 phleb</Text>
            </HStack>
            <HStack spacing={1.5}>
              <Box w={3} h={3} borderRadius="sm" bg="#3182ce" />
              <Text>More phlebs</Text>
            </HStack>
            <HStack spacing={1.5}>
              <Box w={3} h={3} borderRadius="sm" bg="#e2e8f0" />
              <Text>Uncovered</Text>
            </HStack>
          </>
        )}
      </HStack>

      {/* Bottom two columns: recent onboards + stats */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {/* Recent onboards */}
        <Card>
          <CardBody>
            <HStack mb={3} spacing={2}>
              <Icon as={FaUserPlus} color="green.500" />
              <Heading size="sm" color="gray.800">Recently Onboarded</Heading>
              <Badge colorScheme="green" fontSize="2xs">{stats.newThisWeek} this week</Badge>
            </HStack>
            {loadingPhlebs ? (
              <Flex py={6} justify="center"><Spinner size="sm" color="blue.500" /></Flex>
            ) : phlebs.length === 0 ? (
              <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                No completed onboardings yet.
              </Text>
            ) : (
              <VStack align="stretch" spacing={2} maxH="380px" overflowY="auto">
                {phlebs.slice(0, 10).map((p) => {
                  const zipCount = Array.isArray(p.service_zips) ? p.service_zips.length : 0;
                  const isFocused = focusedPhlebId === p.id;
                  return (
                    <Flex
                      key={p.id}
                      p={2.5}
                      borderRadius="md"
                      cursor="pointer"
                      bg={isFocused ? 'orange.50' : 'gray.50'}
                      border="1px solid"
                      borderColor={isFocused ? 'orange.200' : 'gray.100'}
                      _hover={{ borderColor: 'blue.300' }}
                      onClick={() => setFocusedPhlebId(isFocused ? null : p.id)}
                      align="center"
                      justify="space-between"
                    >
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight="700" noOfLines={1} color="gray.800">
                          {p.company_name || p.full_name}
                        </Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {p.onboarding_completed_at
                            ? new Date(p.onboarding_completed_at).toLocaleDateString()
                            : new Date(p.created_at).toLocaleDateString()}
                          {p.email ? ` · ${p.email}` : ''}
                        </Text>
                      </Box>
                      <HStack spacing={2} flexShrink={0}>
                        <Tooltip
                          label={p.supplies_picked_up_at
                            ? `Supplies picked up ${new Date(p.supplies_picked_up_at).toLocaleDateString()}`
                            : 'Supplies not picked up yet'}
                          placement="top"
                        >
                          <Tag
                            size="sm"
                            colorScheme={p.supplies_picked_up_at ? 'green' : 'gray'}
                            variant={p.supplies_picked_up_at ? 'solid' : 'subtle'}
                          >
                            <Icon as={p.supplies_picked_up_at ? FaCheckCircle : FaBoxOpen} boxSize={2.5} mr={1} />
                            {p.supplies_picked_up_at ? 'Supplies' : 'No supplies'}
                          </Tag>
                        </Tooltip>
                        <Badge colorScheme="blue" fontSize="2xs">{zipCount} ZIP{zipCount === 1 ? '' : 's'}</Badge>
                        {isFocused && <Badge colorScheme="orange" fontSize="2xs">on map</Badge>}
                      </HStack>
                    </Flex>
                  );
                })}
                {phlebs.length > 10 && (
                  <Text fontSize="2xs" color="gray.400" textAlign="center" pt={1}>
                    + {phlebs.length - 10} more
                  </Text>
                )}
                {focusedPhlebId && (
                  <Button size="xs" variant="ghost" onClick={() => setFocusedPhlebId(null)} colorScheme="orange">
                    Clear map highlight
                  </Button>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* Stats tiles */}
        <Card>
          <CardBody>
            <HStack mb={3} spacing={2}>
              <Icon as={FaCheckCircle} color="blue.500" />
              <Heading size="sm" color="gray.800">Coverage Stats</Heading>
            </HStack>
            <SimpleGrid columns={2} spacing={3}>
              <Stat label="Covered ZIPs" value={`${stats.coveredZips} / ${stats.totalZipsInCity}`} color="blue" icon={FaCheckCircle} />
              <Stat label="Uncovered ZIPs" value={String(stats.uncovered)} color="orange" icon={FaExclamationTriangle} onClick={openUncovered} clickable />
              <Stat label="Avg phlebs / ZIP" value={stats.avg.toFixed(1)} color="purple" icon={FaUsers} />
              <Stat label="New this week" value={String(stats.newThisWeek)} color="green" icon={FaUserPlus} />
              <Stat label="Supplies picked up" value={`${stats.suppliesPickedUp} / ${phlebs.length}`} color="teal" icon={FaBoxOpen} />
            </SimpleGrid>
            {stats.topPhleb && (
              <>
                <Divider my={3} />
                <HStack>
                  <Icon as={FaTrophy} color="yellow.500" />
                  <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase">Top coverage</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="700" mt={1}>
                  {stats.topPhleb.company_name || stats.topPhleb.full_name}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {stats.topPhleb.service_zips?.length || 0} ZIPs
                </Text>
              </>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Uncovered ZIPs modal */}
      <Modal isOpen={isUncoveredOpen} onClose={closeUncovered} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Uncovered ZIPs ({uncoveredZips.length})</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.500" mb={3}>
              ZIP codes in {city.name} where no onboarded phleb has signed up to cover. Good candidates for recruitment.
            </Text>
            <Wrap>
              {uncoveredZips.map((z) => (
                <WrapItem key={z}>
                  <Tag size="sm" colorScheme="orange" variant="subtle">{z}</Tag>
                </WrapItem>
              ))}
            </Wrap>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={closeUncovered}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ZIP detail modal */}
      <Modal isOpen={isZipOpen} onClose={closeZip} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ZIP {focusedZip}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {(() => {
              const covering = zipToPhlebs.get(focusedZip) || [];
              if (covering.length === 0) {
                return (
                  <Box py={4} textAlign="center">
                    <Icon as={FaExclamationTriangle} color="orange.400" boxSize={8} mb={2} />
                    <Text fontWeight="700">No phleb covers this ZIP yet.</Text>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Recruit a phleb for this area to expand coverage.
                    </Text>
                  </Box>
                );
              }
              return (
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Covered by {covering.length} phleb{covering.length === 1 ? '' : 's'}:
                  </Text>
                  {covering.map((p) => (
                    <Box key={p.id} p={2.5} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                      <Flex justify="space-between" align="center" gap={2}>
                        <Text fontSize="sm" fontWeight="700" noOfLines={1}>
                          {p.company_name || p.full_name}
                        </Text>
                        <Tag
                          size="sm"
                          flexShrink={0}
                          colorScheme={p.supplies_picked_up_at ? 'green' : 'gray'}
                          variant={p.supplies_picked_up_at ? 'solid' : 'subtle'}
                        >
                          <Icon as={p.supplies_picked_up_at ? FaCheckCircle : FaBoxOpen} boxSize={2.5} mr={1} />
                          {p.supplies_picked_up_at ? 'Supplies' : 'No supplies'}
                        </Tag>
                      </Flex>
                      <Text fontSize="xs" color="gray.500">
                        {p.email || '—'} · {p.service_zips?.length || 0} total ZIPs · onboarded {p.onboarding_completed_at ? new Date(p.onboarding_completed_at).toLocaleDateString() : '—'}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={closeZip}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function Stat({ label, value, color, icon, clickable, onClick }) {
  return (
    <Tooltip label={clickable ? 'Click for details' : ''} placement="top" isDisabled={!clickable}>
      <Box
        p={3}
        borderRadius="md"
        bg={`${color}.50`}
        border="1px solid"
        borderColor={`${color}.100`}
        cursor={clickable ? 'pointer' : 'default'}
        _hover={clickable ? { bg: `${color}.100`, borderColor: `${color}.300` } : {}}
        onClick={clickable ? onClick : undefined}
        transition="all 0.15s"
      >
        <HStack spacing={1.5} mb={0.5}>
          <Icon as={icon} color={`${color}.500`} boxSize={3} />
          <Text fontSize="2xs" color={`${color}.700`} fontWeight="700" textTransform="uppercase" letterSpacing="0.5px">
            {label}
          </Text>
        </HStack>
        <Text fontSize="xl" fontWeight="800" color="gray.800" lineHeight="1.1">
          {value}
        </Text>
      </Box>
    </Tooltip>
  );
}
